import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell
} from 'recharts';
import { Lightning, Warning, UsersThree, Truck, Timer, GearSix } from '@phosphor-icons/react';

const CATEGORY_CONFIG = {
  'Energy Instability': { color: '#facc15', icon: Lightning },
  'Air/Steam/Water Inefficiency': { color: '#60a5fa', icon: Lightning },
  'Scrap & Rework': { color: '#ef4444', icon: Warning },
  'Safety & People Factors': { color: '#c084fc', icon: UsersThree },
  'Supplier & Material Issues': { color: '#f97316', icon: Truck },
  'Changeover & Setup': { color: '#2dd4bf', icon: Timer },
  'Maintenance & Mechanical': { color: '#94a3b8', icon: GearSix },
};

function fmtVnd(n) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return n?.toLocaleString?.() ?? n;
}

function CustomTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-[#1a1a1a] border border-border rounded-lg p-3 shadow-lg text-xs font-body max-w-xs">
      <p className="font-semibold text-text-primary mb-1">{d.category}</p>
      <p className="text-text-secondary">Loss: <span className="text-red-400 font-medium">{d.loss_pct}%</span> ({d.loss_minutes} min)</p>
      <p className="text-text-secondary">Impact: <span className="text-amber-400 font-medium">{fmtVnd(d.vnd_impact)} VND</span></p>
      <p className="text-text-secondary">ESG: <span className="text-teal-400">{d.esg_alignment}</span></p>
    </div>
  );
}

export default function OeeWaterfall({ data }) {
  const waterfall = data?.waterfall || [];
  if (!waterfall.length) return null;

  const totalLoss = waterfall.reduce((s, w) => s + w.loss_pct, 0);
  const totalVnd = waterfall.reduce((s, w) => s + w.vnd_impact, 0);
  const chartData = waterfall.map(w => ({
    ...w,
    short: w.category.length > 16 ? w.category.slice(0, 16) + '...' : w.category,
  }));

  return (
    <div className="space-y-5 animate-fade-in" data-testid="oee-waterfall">
      {/* Summary strip */}
      <div className="bg-surface border border-border rounded-lg p-4 flex flex-wrap items-center gap-6">
        <div>
          <p className="text-[10px] font-body font-semibold uppercase tracking-wider text-text-secondary">Total OEE Loss</p>
          <p className="font-heading text-2xl font-bold text-red-400">{totalLoss.toFixed(1)}%</p>
        </div>
        <div className="border-l border-border pl-6">
          <p className="text-[10px] font-body font-semibold uppercase tracking-wider text-text-secondary">VND Impact / Day</p>
          <p className="font-heading text-2xl font-bold text-amber-400">{fmtVnd(totalVnd)} VND</p>
        </div>
        <div className="border-l border-border pl-6">
          <p className="text-[10px] font-body font-semibold uppercase tracking-wider text-text-secondary">Categories</p>
          <p className="font-heading text-2xl font-bold text-text-primary">{waterfall.length}</p>
        </div>
      </div>

      {/* Waterfall Chart */}
      <div className="bg-surface border border-border rounded-lg p-5">
        <h2 className="font-heading text-base font-semibold text-text-primary mb-4">OEE Loss Waterfall — What's Eating Today's OEE?</h2>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} layout="vertical" margin={{ left: 10, right: 30 }}>
              <XAxis type="number" tick={{ fill: '#A3A380', fontSize: 11 }} unit="%" />
              <YAxis dataKey="short" type="category" width={140} tick={{ fill: '#A3A380', fontSize: 11 }} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="loss_pct" radius={[0, 4, 4, 0]} barSize={22}>
                {chartData.map((entry, idx) => (
                  <Cell key={idx} fill={CATEGORY_CONFIG[entry.category]?.color || '#6b7280'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Root Cause Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {waterfall.map((w, idx) => {
          const cfg = CATEGORY_CONFIG[w.category] || { color: '#6b7280', icon: Warning };
          const Icon = cfg.icon;
          return (
            <div key={idx} className="bg-surface border border-border rounded-lg p-4 hover:border-tertiary/40 transition-all" data-testid={`oee-loss-${idx}`}>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: cfg.color + '18' }}>
                  <Icon size={18} weight="duotone" style={{ color: cfg.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <p className="font-heading text-sm font-semibold text-text-primary truncate">{w.category}</p>
                    <span className="text-xs font-body font-semibold text-red-400 shrink-0 ml-2">-{w.loss_pct}%</span>
                  </div>
                  <div className="flex gap-3 text-[10px] font-body text-text-secondary mb-2 uppercase tracking-wider">
                    <span>{w.loss_minutes} min</span>
                    <span>{fmtVnd(w.vnd_impact)} VND</span>
                    <span className="text-teal-400">ESG: {w.esg_alignment}</span>
                  </div>
                  <ul className="space-y-1">
                    {w.root_causes.map((rc, i) => (
                      <li key={i} className="text-xs font-body text-text-secondary flex items-start gap-1.5">
                        <span className="w-1 h-1 rounded-full mt-1.5 shrink-0" style={{ backgroundColor: cfg.color }} />
                        {rc}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
